import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Select from "react-select";
import DataTable from 'react-data-table-component';
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import Swal from "sweetalert2";
import IsLogin from "../../../Common/IsLogin";
import { LoaderComponent } from "../../../Common/LoaderComponent";

const CashFlowStatement = () => {
  const location = useLocation();
  const [receiveData, setReceiveData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  // Utility functions to get current month date range
  const getCurrentMonthRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    
    // First day of current month
    const firstDay = new Date(year, month, 1);
    // Last day of current month
    const lastDay = new Date(year, month + 1, 0);
    
    return {
      start_date: firstDay.toISOString().split('T')[0], // Format: YYYY-MM-DD
      end_date: lastDay.toISOString().split('T')[0]     // Format: YYYY-MM-DD
    };
  };

  const [inputs, setInputs] = useState(() => {
    const currentMonthRange = getCurrentMonthRange();
    return {
      start_date: currentMonthRange.start_date,
      end_date: currentMonthRange.end_date,
      bank_id: "",
      type: "",
    };
  });
  const [alert, setAlert] = useState(null);

  const transactionTypes = [
    { value: "", label: "All Types" },
    { value: "In", label: "In" },
    { value: "Out", label: "Out" },
  ];

  // DataTable columns configuration
  const columns = [
    {
      name: 'Date',
      selector: row => row.date,
      sortable: true,
      width: '120px',
      cell: row => <div className="text-sm font-weight-bold">{row.date}</div>
    },
    {
      name: 'Transaction Type',
      selector: row => row.transaction_type,
      sortable: true,
      width: '140px',
      cell: row => (
        <span className={`badge ${row.transaction_type === 'In' ? 'bg-success' : 'bg-danger'}`}>
          {row.transaction_type}
        </span>
      )
    },
    {
      name: 'Flag',
      selector: row => row.flag,
      sortable: true,
      width: '160px',
      cell: row => <div className="text-sm">{row.flag || 'N/A'}</div>
    },
    {
      name: 'Bank Information',
      selector: row => row.bank_information?.name,
      sortable: true,
      width: '200px',
      cell: row => (
        <div className="text-sm">
          <strong>{row.bank_information?.name}</strong><br />
          <small>A/C: {row.bank_information?.account_number}</small><br />
          <small>Branch: {row.bank_information?.branch}</small>
        </div>
      )
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      sortable: true,
      width: '120px',
      cell: row => (
        <div className={`text-sm font-weight-bold ${row.transaction_type === 'In' ? 'text-success' : 'text-danger'}`}>
          {row.transaction_type === 'In' ? '+' : '-'} {row.amount}
        </div>
      )
    },
    {
      name: 'Description',
      selector: row => row.description,
      sortable: true,
      width: '200px',
      cell: row => <div className="text-sm">{row.description || 'N/A'}</div>
    }
  ];

  // Custom styles for DataTable
  const customStyles = {
    header: {
      style: {
        minHeight: '56px',
      },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid',
        borderTopWidth: '1px',
        borderTopColor: '#e3e6f0',
        backgroundColor: '#f8f9fc',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#e3e6f0',
        },
        fontSize: '12px',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: '#5a5c69',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': {
          borderRightStyle: 'solid',
          borderRightWidth: '1px',
          borderRightColor: '#e3e6f0',
        },
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: '13px',
      },
    },
    rows: {
      style: {
        '&:nth-of-type(odd)': {
          backgroundColor: '#f8f9fc',
        },
        '&:hover': {
          backgroundColor: '#e3e6f0',
          cursor: 'pointer',
        },
      },
    },
  };

  const fetchData = () => {
    setLoading(true);
    const params = new URLSearchParams();
    
    if (inputs.start_date) params.append('start_date', inputs.start_date);
    if (inputs.end_date) params.append('end_date', inputs.end_date);
    if (inputs.bank_id && inputs.bank_id !== "") params.append('bank_id', inputs.bank_id);
    if (inputs.type && inputs.type !== "") params.append('type', inputs.type);

    console.log("Fetching data with params:", params.toString());
    console.log("Current inputs:", inputs);

    Http.get(`/accounts/cash-flow-statement?${params.toString()}`).then((res) => {
      setReceiveData(res.data.data);
      // Only update banks if they haven't been set yet
      if (banks.length === 0) {
        setBanks(res.data.banks.map(bank => ({
          value: bank.id,
          label: `${bank.name} - ${bank.account_number}`
        })));
      }
      setLoading(false);
    }).catch((error) => {
      console.error("Error fetching cash flow data:", error);
      setLoading(false);
      showAlert("Error fetching cash flow data", "error");
    });
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  // Auto-fetch data when filters change (with debounce)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (inputs.start_date || inputs.end_date || inputs.bank_id || inputs.type) {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [inputs.start_date, inputs.end_date, inputs.bank_id, inputs.type]);

  // Check for month change every hour and update date range if needed
  useEffect(() => {
    const checkMonthChange = () => {
      const currentMonthRange = getCurrentMonthRange();
      if (inputs.start_date !== currentMonthRange.start_date || 
          inputs.end_date !== currentMonthRange.end_date) {
        // Only update if the current dates match a previous month's range
        const currentDate = new Date();
        const inputStartDate = new Date(inputs.start_date);
        const inputEndDate = new Date(inputs.end_date);
        
        // Check if the current input dates represent a full month range
        const isFullMonthRange = inputStartDate.getDate() === 1 && 
                                inputEndDate.getDate() === new Date(inputEndDate.getFullYear(), inputEndDate.getMonth() + 1, 0).getDate();
        
        if (isFullMonthRange && inputStartDate.getMonth() !== currentDate.getMonth()) {
          console.log("Month changed, updating date range to current month");
          refreshCurrentMonth();
        }
      }
    };

    // Check immediately and then every hour
    checkMonthChange();
    const interval = setInterval(checkMonthChange, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [inputs.start_date, inputs.end_date]);

  const showAlert = (message, type) => {
    setAlert({
      message: message,
      type: type,
    });
    setTimeout(() => {
      setAlert(null);
    }, 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const selectHandleChange = (selectedOption, actionMeta) => {
    const name = actionMeta.name;
    const value = selectedOption ? selectedOption.value : "";
    console.log("Select change:", name, value, selectedOption);
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const formSubmit = () => {
    fetchData();
  };

  const clearFilters = () => {
    const currentMonthRange = getCurrentMonthRange();
    setInputs({
      start_date: currentMonthRange.start_date,
      end_date: currentMonthRange.end_date,
      bank_id: "",
      type: "",
    });
    setTimeout(() => {
      fetchData();
    }, 100);
  };

  const refreshCurrentMonth = () => {
    const currentMonthRange = getCurrentMonthRange();
    setInputs(prevInputs => ({
      ...prevInputs,
      start_date: currentMonthRange.start_date,
      end_date: currentMonthRange.end_date,
    }));
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <LoaderComponent />
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <h5 className="am-title">Cash Flow Statement</h5>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            <div className="card pd-sm-40">
              <div className="row">
                <div className="col-lg-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="start_date"
                    value={inputs.start_date || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="end_date"
                    value={inputs.end_date || ""}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Bank</label>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    value={
                      inputs.bank_id === "" || inputs.bank_id === null
                        ? { value: "", label: "All Banks" }
                        : [...[{ value: "", label: "All Banks" }], ...banks].find(bank => bank.value === inputs.bank_id) || { value: "", label: "All Banks" }
                    }
                    name="bank_id"
                    options={[{ value: "", label: "All Banks" }, ...banks]}
                    onChange={selectHandleChange}
                    isClearable
                    placeholder="Select Bank..."
                  />
                </div>
                <div className="col-lg-3">
                  <label className="form-label">Transaction Type</label>
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    value={
                      transactionTypes.find(type => type.value === inputs.type) || transactionTypes[0]
                    }
                    name="type"
                    options={transactionTypes}
                    onChange={selectHandleChange}
                    placeholder="Select Type..."
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-lg-4">
                  <button
                    className="btn btn-primary btn-block mg-b-10"
                    onClick={formSubmit}
                    disabled={loading}
                  >
                    <i className="fa fa-search mg-r-10"></i> 
                    {loading ? "Searching..." : "Search"}
                  </button>
                </div>
                <div className="col-lg-4">
                  <button
                    className="btn btn-info btn-block mg-b-10"
                    onClick={refreshCurrentMonth}
                    title="Set date range to current month"
                  >
                    <i className="fa fa-calendar mg-r-10"></i> Current Month
                  </button>
                </div>
                <div className="col-lg-4">
                  <button
                    className="btn btn-secondary btn-block mg-b-10"
                    onClick={clearFilters}
                    title="Reset all filters to current month defaults"
                  >
                    <i className="fa fa-refresh mg-r-10"></i> Reset Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="am-pagebody">
            <div className="card pd-20 pd-sm-40">
              <div className="row">
                <div className="col-12">
                  <h6 className="card-body-title mg-sm-b-30">
                    Cash Flow Statement ({receiveData.length} records)
                    {inputs.start_date && inputs.end_date && (
                      <small className="text-muted ml-3">
                        <i className="fa fa-calendar mg-r-5"></i>
                        {new Date(inputs.start_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })} - {new Date(inputs.end_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                        {(() => {
                          const currentMonthRange = getCurrentMonthRange();
                          const isCurrentMonth = inputs.start_date === currentMonthRange.start_date && 
                                                inputs.end_date === currentMonthRange.end_date;
                          return isCurrentMonth ? (
                            <span className="badge badge-success ml-2">Current Month</span>
                          ) : null;
                        })()}
                      </small>
                    )}
                  </h6>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <DataTable
                    columns={columns}
                    data={receiveData}
                    pagination
                    paginationPerPage={25}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    progressPending={loading}
                    progressComponent={
                      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    }
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="fa fa-info-circle fa-2x text-muted mb-2"></i>
                        <p className="text-muted">No transactions found</p>
                      </div>
                    }
                    customStyles={customStyles}
                    highlightOnHover
                    striped
                    responsive
                    fixedHeader
                    fixedHeaderScrollHeight="600px"
                    selectableRows={false}
                    dense
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashFlowStatement;